/* Umbrella Union — Media Coordinator (ASL: Zero Overlap)
 * Coordinates HTML5 <audio> and YouTube iframe players so only one plays at a time.
 * Theme Editor safe: idempotent registration per section.
 */
(function () {
  'use strict';

  const ROOT = (window.UmbrellaUnion = window.UmbrellaUnion || {});

  function loadYouTubeAPI() {
    if (ROOT.__ytApiPromise) return ROOT.__ytApiPromise;

    ROOT.__ytApiPromise = new Promise((resolve) => {
      if (window.YT && window.YT.Player) return resolve(window.YT);

      const existing = document.querySelector('script[data-uu-yt-api]');
      if (existing) {
        const check = () => (window.YT && window.YT.Player ? resolve(window.YT) : setTimeout(check, 50));
        return check();
      }

      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      tag.async = true;
      tag.defer = true;
      tag.dataset.uuYtApi = '1';
      document.head.appendChild(tag);

      const prior = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = function () {
        try {
          if (typeof prior === 'function') prior();
        } catch (e) {}
        resolve(window.YT);
      };
    });

    return ROOT.__ytApiPromise;
  }

  class Coordinator {
    constructor() {
      this.audioBySection = new Map();
      this.ytBySection = new Map();
    }

    registerSection(sectionId, opts) {
      if (!sectionId) return;
      const root = opts && opts.root ? opts.root : document;

      // HTML5 audio
      const audios = Array.from(root.querySelectorAll('audio[data-uu-audio]'));
      if (audios.length) {
        this.audioBySection.set(sectionId, audios);
        audios.forEach((audio) => {
          if (audio.dataset.uuBound === '1') return;
          audio.dataset.uuBound = '1';
          audio.addEventListener('play', () => this.pauseAllExcept({ type: 'audio', el: audio }));
        });
      }

      // YouTube iframes
      const ytFrames = Array.from(root.querySelectorAll('iframe[data-uu-youtube]'));
      if (ytFrames.length) {
        loadYouTubeAPI().then((YT) => {
          const players = [];
          ytFrames.forEach((iframe) => {
            if (iframe.dataset.uuPlayerBound === '1') return;
            iframe.dataset.uuPlayerBound = '1';
            try {
              const player = new YT.Player(iframe, {
                events: {
                  onStateChange: (evt) => {
                    // 1 = playing
                    if (evt && evt.data === 1) this.pauseAllExcept({ type: 'youtube', el: player });
                  },
                },
              });
              players.push(player);
            } catch (e) {}
          });
          if (players.length) this.ytBySection.set(sectionId, players);
        });
      }
    }

    unregisterSection(sectionId) {
      if (!sectionId) return;
      this.audioBySection.delete(sectionId);

      const players = this.ytBySection.get(sectionId);
      if (players && players.length) {
        players.forEach((p) => {
          try {
            if (p && typeof p.destroy === 'function') p.destroy();
          } catch (e) {}
        });
      }
      this.ytBySection.delete(sectionId);
    }

    pauseAllExcept(current) {
      // Pause all HTML5 audio
      this.audioBySection.forEach((audios) => {
        audios.forEach((a) => {
          if (current && current.type === 'audio' && current.el === a) return;
          try {
            if (!a.paused) a.pause();
          } catch (e) {}
        });
      });

      // Pause all YouTube
      this.ytBySection.forEach((players) => {
        players.forEach((p) => {
          if (current && current.type === 'youtube' && current.el === p) return;
          try {
            if (p && typeof p.pauseVideo === 'function') p.pauseVideo();
          } catch (e) {}
        });
      });
    }
  }

  if (!ROOT.mediaCoordinator) ROOT.mediaCoordinator = new Coordinator();
})();
